// src/components/upload-area/UploadArea.tsx
import { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { Icon } from '@iconify/react';
import { toast } from 'src/components/snackbar';

import {
  executeAdminActions,
  executeUserActions,
} from 'src/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import { UploadBox } from '../upload';

interface UploadAreaProps {
  field: any; // full JSON node for this upload_area
  disabled: boolean;
  module_name?: string;
  sub_module?: string;
  paramsData: Record<string, any>;
  isAdmin: boolean;
  defaultData: any;
  bannerDetails: any;
}

export default function UploadArea({
  field,
  disabled,
  module_name = '',
  sub_module = '',
  paramsData,
  isAdmin,
  defaultData,
  bannerDetails,
}: UploadAreaProps) {
  const { file_parameters, upload_files_sql_data, view_sql_data, delete_files_sql_data } = field;

  const [existing, setExisting] = useState<{ fileContentId: string; fileName: string }[]>([]);
  //   const [progress, setProgress] = useState<Map<string, number>>(new Map());
  // (Fetch doesn’t expose onUploadProgress, so we’ll omit the progress Map
  // entirely to keep ESLint happy.  You can wire it back later with XHR /
  // axios if you need granular progress.)
  /* ------------------------------------------------------------------ */
  /* 1️⃣  Fetch already–uploaded files                                   */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!view_sql_data) return;

    (async () => {
      try {
        const payload = {
          sql_data: view_sql_data,
          RelatedRecordId: defaultData?.RecordId || paramsData?.RelatedRecordId,
          user_id: bannerDetails?.UserId,
        };
        const res = await executeUserActions(module_name!, {
          tk: EncryptUtils.encryptedData(payload),
        });
        // expect [{ FileContentId, FileName }]
        setExisting(
          (res?.rows || []).map((r: any) => ({
            fileContentId: r.FileContentId,
            fileName: r.FileName,
          }))
        );
      } catch {
        toast.warning('Unable to retrieve files');
      }
    })();
  }, [view_sql_data, module_name, defaultData, paramsData, bannerDetails]);

  /* ------------------------------------------------------------------ */
  /* 2️⃣  Client-side validators                                         */
  /* ------------------------------------------------------------------ */
  const isFileOk = (file: File) => {
    const { accept_file_size_bytes, accept_file_types } = file_parameters;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (
      accept_file_types &&
      !accept_file_types.map((x: string) => x.toLowerCase()).includes(ext!)
    ) {
      toast.error(`.${ext} not allowed`);
      return false;
    }
    if (accept_file_size_bytes && file.size > accept_file_size_bytes) {
      toast.error(`${file.name} is too large`);
      return false;
    }
    return true;
  };

  /* ------------------------------------------------------------------ */
  /* 3️⃣  Upload flow                                                     */
  /* ------------------------------------------------------------------ */
  const handleDrop = async (files: File[]) => {
    const tasks = files // 1️⃣ filter client-side
      .filter(isFileOk)
      .map(async (file) => {
        // 2️⃣ build one async task per file
        try {
          /* 3a. POST raw file to Blob-upload endpoint */
          const form = new FormData();
          form.append('file', file);
          const upRes = await fetch('/api/upload-to-blob', {
            method: 'POST',
            body: form,
          });
          if (!upRes.ok) throw new Error('blob upload failed');
          const { fileContentId } = await upRes.json();

          /* 3b. Register blob in DB */
          const sqlPayload = {
            sql_data: upload_files_sql_data,
            RelatedRecordId: defaultData?.RecordId || paramsData?.RelatedRecordId,
            FileContentId: fileContentId,
            RequirementCode: field.code ?? null,
            user_id: bannerDetails?.UserId,
          };
          await (isAdmin ? executeAdminActions : executeUserActions)(module_name, sub_module, {
            tk: EncryptUtils.encryptedData(sqlPayload),
          });

          toast.success(`${file.name} uploaded`);
          setExisting((prev) => [...prev, { fileContentId, fileName: file.name }]);
        } catch (err) {
          toast.error(`Failed to upload ${file.name}`);
        }
      });

    /* 4️⃣ run them all in parallel  — no “await in loop” */
    await Promise.all(tasks);
  };

  /* ------------------------------------------------------------------ */
  /* 4️⃣  Delete flow                                                     */
  /* ------------------------------------------------------------------ */
  const deleteFile = async (fileContentId: string) => {
    try {
      const payload = {
        sql_data: delete_files_sql_data,
        FileContentId: fileContentId,
        user_id: bannerDetails?.UserId,
      };
      await (isAdmin ? executeAdminActions : executeUserActions)(module_name!, sub_module, {
        tk: EncryptUtils.encryptedData(payload),
      });
      setExisting((prev) => prev.filter((f) => f.fileContentId !== fileContentId));
    } catch {
      toast.error('Delete failed');
    }
  };

  /* ------------------------------------------------------------------ */
  /* 5️⃣  UI                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {/* drop target */}
      <UploadBox
        multiple={false}
        disabled={disabled}
        onDrop={handleDrop}
        placeholder={<Typography variant="caption">Drag or click to add {field.label}</Typography>}
        accept={{ '*/*': [] }}
        sx={{ width: '100%' }}
      />

      {/* progress (simple) */}
      {/* {Array.from(progress.entries()).map(([name, pct]) => (
        <Stack key={name} spacing={0.5}>
          <Typography variant="caption">{name}</Typography>
          <LinearProgress variant="determinate" value={pct} />
        </Stack>
      ))} */}

      {/* already uploaded */}
      {existing.map((f) => (
        <Paper
          variant="outlined"
          key={f.fileContentId}
          sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Icon icon="mdi:file" />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {f.fileName}
          </Typography>
          {!disabled && (
            <IconButton size="small" onClick={() => deleteFile(f.fileContentId)}>
              <Icon icon="mdi:trash-can-outline" />
            </IconButton>
          )}
        </Paper>
      ))}
    </Stack>
  );
}
